import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  X, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle,
  Loader2, RefreshCw, ChevronRight,
} from 'lucide-react';

import { fetchAllEmployees } from '../../store/slices/employeeSlice';
import employeeService from '../../services/EmployeeService';
import designationService from '../../services/DesignationService';
import departmentService from '../../services/DepartmentService';
import locationService from '../../services/LocationService';
import gradeService from '../../services/GradeService';
import shiftService from '../../services/ShiftService';
import employeeCategoryService from '../../services/EmployeeCategoryService';
import bankService from '../../services/BankService';
import bankBranchService from '../../services/BankBranchService';

import { getLoggedInServiceNo } from '../../utils/masterDataUtils';
import { isMasterApproved } from '../../utils/masterApproval';
import { NEW_EMPLOYEE_IS_APPROVED } from '../../utils/employeeApproval';
import { NEW_BANK_IS_APPROVED } from '../../utils/employeeBankApproval';
import {
  COLUMNS, downloadTemplate, readWorkbook, validateRow,
  findDuplicateServiceNos, buildAddEmployeePayload, buildEmploymentFixUp,
  buildAddressPayloads, buildBankPayload, buildContactPayload,
} from '../../utils/employeeImport';

/* ── Reading the master lists ─────────────────────────────────────────────── */

const fmt = (v) => (v === null || v === undefined ? '' : String(v).trim());

const pick = (row, keys) => {
  for (const k of keys) {
    if (row && row[k] !== undefined && row[k] !== null && fmt(row[k]) !== '') return fmt(row[k]);
  }
  return '';
};

const rowsOf = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  const body = res.data !== undefined && res.data !== null ? res.data : res;
  const set = body.ResultSet ?? body.resultSet ?? body.Result ?? body.Data ?? null;
  return Array.isArray(set) ? set : [];
};

/**
 * Master rows come back with different column names per endpoint, so each list
 * is read with explicit keys plus generic fallbacks.
 *
 * The name is picked BEFORE falling back to the id: if a designation's name
 * column isn't matched, every entry would show its code instead, and then a
 * sheet saying "DRIVER" could never match. Any key ending in NAME or DESC is
 * accepted as a last resort for exactly that reason.
 */
const nameFallback = (row) => {
  const key = Object.keys(row || {}).find(
    (k) => /(_NAME|_DESC|NAME|DESCRIPTION)$/i.test(k) && fmt(row[k]) !== ''
  );
  return key ? fmt(row[key]) : '';
};

const toList = (rows, idKeys, nameKeys, codeKeys = []) =>
  rows.map((r) => {
    const id = pick(r, [...idKeys, 'Id', 'id']);
    const code = pick(r, [...codeKeys, 'Code', 'code']);
    const name = pick(r, [...nameKeys, 'Name', 'name', 'Description', 'description'])
      || nameFallback(r)
      || code
      || id;
    return id ? { id, name, code } : null;
  }).filter(Boolean);

async function loadLookups() {
  const settle = (fn) => fn().then(rowsOf).catch(() => []);

  const [desig, dept, loc, grade, shift, cat, bank, branch, emp] = await Promise.all([
    settle(() => designationService.GetDesignationList()),
    settle(() => departmentService.GetDepartmentList()),
    settle(() => locationService.GetLocationList()),
    settle(() => gradeService.GetGradeList()),
    settle(() => shiftService.GetShiftList()),
    settle(() => employeeCategoryService.GetEmployeeCategoryList()),
    settle(() => bankService.GetBankList()),
    settle(() => bankBranchService.getBankBranchList()),
    settle(() => employeeService.GetEmployeeList()),
  ]);

  // Only approved banks and branches, exactly as the Add form filters them.
  const banks = toList(bank.filter(isMasterApproved),
    ['HBD_BANK_ID', 'BankId', 'bankId', 'BankID'],
    ['HBD_BANK_NAME', 'BankName', 'bankName'],
    ['HBD_BANK_CODE', 'HBD_SHORT_NAME', 'BankCode']);

  const branches = branch.filter(isMasterApproved).map((b) => {
    const id = pick(b, ['HBB_BRANCH_ID', 'BranchId', 'branchId', 'BranchID']);
    const name = pick(b, ['HBB_BRANCH_NAME', 'BranchName', 'branchName']) || nameFallback(b);
    const code = pick(b, ['HBB_BRANCH_CODE', 'BranchCode', 'branchCode']);
    const bankId = pick(b, ['HBB_BANK_ID', 'BankId', 'bankId', 'BankID']);
    const bankName = (banks.find((x) => String(x.id) === String(bankId)) || {}).name || '';
    return id ? { id, name: name || code || id, code, bankId, bankName } : null;
  }).filter(Boolean);

  /* Reporting Employee — the same option list the Add form's dropdown builds:
     matched on Staff ID, on the name, or on the "Name (STAFFID)" label. */
  const employees = emp.map((e) => {
    const id = pick(e, ['HED_EMPLOYEE_ID', 'HedEmployeeId', 'EmployeeId', 'hedEmployeeId', 'employeeId', 'Id', 'id']);
    const code = pick(e, ['HED_SERVICE_NO', 'HedServiceNo', 'ServiceNo', 'serviceNo', 'p_ced_service_no']);
    const reportName = pick(e, ['HED_REPORT_NAME', 'HedReportName', 'ReportName', 'reportName', 'p_report_name']);
    const first = pick(e, ['HED_FIRST_NAME', 'HedFirstName', 'FirstName', 'firstName', 'p_first_name']);
    const last = pick(e, ['HED_LAST_NAME', 'HedLastName', 'LastName', 'lastName', 'p_last_name']);
    const name = reportName || `${first} ${last}`.trim() || code || id;
    return id ? { id, name, code } : null;
  }).filter(Boolean);

  return {
    employees,
    designations: toList(desig,
      ['DesignationId', 'designationId', 'HDS_DESIGNATION_ID', 'HED_DESIGNATION_ID'],
      ['DesignationName', 'designationName', 'HDS_DESIGNATION_NAME', 'HDS_DESIGNATION_DESC', 'HED_DESIGNATION_NAME', 'DesignationDesc'],
      ['HDS_DESIGNATION_CODE', 'DesignationCode', 'HED_DESIGNATION_CODE', 'p_des_code']),
    departments:  toList(dept,
      ['DepartmentId', 'departmentId', 'HDD_DEPARTMENT_ID', 'HDD_DEP_ID'],
      ['DepartmentName', 'departmentName', 'HDD_DEPARTMENT_NAME', 'HDD_DEP_NAME', 'HDD_DEP_DESC'],
      ['HDD_DEPARTMENT_CODE', 'HDD_DEP_CODE', 'DepartmentCode']),
    locations:    toList(loc,
      ['LocationId', 'locationId', 'HLD_LOCATION_ID', 'HLD_LOC_ID'],
      ['LocationName', 'locationName', 'HLD_LOCATION_NAME', 'HLD_LOC_NAME', 'HLD_LOC_DESC'],
      ['HLD_LOC_CODE', 'LocationCode']),
    grades:       toList(grade,
      ['GradeId', 'gradeId', 'HGC_GRADE_ID', 'HGD_GRADE_ID'],
      ['GradeName', 'gradeName', 'HGC_GRADE_NAME', 'HGD_GRADE_NAME', 'HGC_GRADE_DESC'],
      ['HGC_GRADE_CODE', 'HGD_GRADE_CODE', 'GradeCode']),
    shifts:       toList(shift,
      ['ShiftId', 'shiftId', 'HSC_SHIFT_ID', 'HSD_SHIFT_ID'],
      ['ShiftName', 'shiftName', 'HSC_SHIFT_NAME', 'HSD_SHIFT_NAME', 'HSC_SHIFT_DESC'],
      ['HSC_SHIFT_CODE', 'HSD_SHIFT_CODE', 'ShiftCode']),
    categories:   toList(cat,
      ['HEC_EMPLOYMENT_CATEGORY_ID', 'CategoryId', 'categoryId', 'HEC_CATEGORY_ID'],
      ['CategoryName', 'categoryName', 'HEC_CATEGORY_NAME', 'HEC_CATEGORY_DESC'],
      ['HEC_CATEGORY_CODE', 'CategoryCode']),
    banks,
    branches,
    existingServiceNos: new Set(
      emp.map((e) => pick(e, ['HED_SERVICE_NO', 'HedServiceNo', 'ServiceNo', 'p_ced_service_no']).toLowerCase())
         .filter(Boolean)
    ),
  };
}

/* ── Component ───────────────────────────────────────────────────────────── */

const BTN_PRIMARY = 'flex items-center gap-2 h-9 px-5 rounded-lg text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_GHOST = 'flex items-center gap-2 h-9 px-4 rounded-lg text-xs font-semibold bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

export default function ImportEmployeesModal({ isOpen, onClose, onImported }) {
  const dispatch = useDispatch();

  const [lookups, setLookups] = useState(null);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);          // { values, errors, _row }
  const [parseError, setParseError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState(null);  // { ok: [], failed: [] }

  const reset = () => {
    setFileName(''); setRows([]); setParseError(null);
    setResults(null); setProgress({ done: 0, total: 0 });
  };

  const refreshLookups = useCallback(async () => {
    setLoadingLookups(true);
    try {
      setLookups(await loadLookups());
    } finally {
      setLoadingLookups(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) { reset(); refreshLookups(); }
  }, [isOpen, refreshLookups]);

  const handleFile = async (file) => {
    if (!file) return;
    reset();
    setFileName(file.name);
    try {
      const parsed = await readWorkbook(file);
      if (!parsed.length) {
        setParseError('No data rows found. Fill in the Employees sheet below the header row.');
        return;
      }
      const dupes = findDuplicateServiceNos(parsed);
      const checked = parsed.map((r) => {
        const { values, errors } = validateRow(r, lookups || {}, (lookups || {}).existingServiceNos || new Set());
        if (dupes.has(fmt(r.serviceNo).toLowerCase())) {
          errors.push('This Staff ID appears more than once in the file');
        }
        return { _row: r._row, values, errors };
      });
      setRows(checked);
    } catch (err) {
      setParseError(err?.message || 'Could not read that file.');
    }
  };

  const valid = useMemo(() => rows.filter((r) => r.errors.length === 0), [rows]);
  const invalid = useMemo(() => rows.filter((r) => r.errors.length > 0), [rows]);

  /* One employee: the same sequence of calls the Add form makes. */
  const importOne = async (v, createdBy) => {
    const addRes = await employeeService.AddEmployee(
      buildAddEmployeePayload(v, createdBy, NEW_EMPLOYEE_IS_APPROVED)
    );

    const body = addRes?.data;
    if (body && body.StatusCode && ![200, 201, 204].includes(body.StatusCode)) {
      throw new Error(body.Message || body.message || 'Server rejected the employee.');
    }

    // The id comes back in a few shapes; fall back to looking it up by Staff ID.
    let employeeId = null;
    if (body) {
      const rs = body.ResultSet ?? body.resultSet ?? body;
      if (typeof rs === 'number') employeeId = rs;
      else if (rs) employeeId = rs.EmployeeId ?? rs.employeeId ?? rs.HedEmployeeId ?? rs.HED_EMPLOYEE_ID ?? null;
    }
    if (!employeeId) {
      const list = rowsOf(await employeeService.GetEmployeeList().catch(() => null));
      const match = list.find((r) =>
        pick(r, ['HED_SERVICE_NO', 'HedServiceNo', 'ServiceNo', 'p_ced_service_no']) === fmt(v.serviceNo));
      if (match) {
        employeeId = match.HED_EMPLOYEE_ID ?? match.HedEmployeeId ?? match.EmployeeId ?? match.id ?? null;
      }
    }
    if (!employeeId) {
      // The employee exists but the follow-ups can't be attached — say so
      // rather than reporting a clean success.
      throw new Error('Employee was created but its id could not be read, so address / bank / contact were skipped.');
    }

    /* Permanent first, then current — one record each, exactly as the Add
       form posts them. A blank address is simply not in the list. */
    for (const address of buildAddressPayloads(v, employeeId, createdBy)) {
      await employeeService.AddEmployeeAddress(address)
        .catch((e) => console.error(`[import] ${address.p_heaAddressType} address`, e));
    }

    const fixUp = buildEmploymentFixUp(v);
    if (fixUp) await employeeService.UpdateEmployee(fixUp).catch((e) => console.error('[import] employment fix-up', e));

    const bank = buildBankPayload(v, employeeId, createdBy, NEW_BANK_IS_APPROVED);
    if (bank) await employeeService.AddBankDetails(bank).catch((e) => console.error('[import] bank', e));

    const contact = buildContactPayload(v, employeeId, createdBy);
    if (contact) await employeeService.AddEmployeeContact(contact).catch((e) => console.error('[import] contact', e));

    return employeeId;
  };

  const runImport = async () => {
    const createdBy = getLoggedInServiceNo();
    setImporting(true);
    setProgress({ done: 0, total: valid.length });

    const ok = [];
    const failed = [];

    // Sequential on purpose: the server assigns Staff IDs and the fallback
    // lookup re-reads the employee list, which parallel writes would race.
    for (let i = 0; i < valid.length; i += 1) {
      const row = valid[i];
      try {
        await importOne(row.values, createdBy);
        ok.push(row);
      } catch (err) {
        failed.push({
          ...row,
          message: err?.response?.data?.Message || err?.message || 'Failed',
        });
      }
      setProgress({ done: i + 1, total: valid.length });
    }

    setResults({ ok, failed });
    setImporting(false);
    if (ok.length) {
      dispatch(fetchAllEmployees());
      if (onImported) onImported(ok.length);
    }
  };

  if (!isOpen) return null;

  const previewCols = COLUMNS.filter((c) =>
    ['serviceNo', 'firstName', 'lastName', 'nicNo', 'designation', 'department', 'dateOfJoining'].includes(c.key));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={importing ? undefined : onClose} />

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={16} className="text-primary-600" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Import Employees from Excel</h2>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Fill in the template, upload it, and every valid row is added as a new employee.
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={importing}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-40">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Step 1 — template */}
          <div className="flex items-center justify-between gap-3 p-4 rounded-xl border border-gray-200 bg-gray-50">
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-gray-800">1. Download the template</p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                It carries the exact column headings, two example rows, and a Reference Lists sheet
                with the department, designation and bank names this server accepts.
              </p>
            </div>
            <button
              onClick={() => downloadTemplate(lookups || {})}
              disabled={loadingLookups}
              className={`${BTN_GHOST} shrink-0`}
            >
              {loadingLookups ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              {loadingLookups ? 'Loading lists…' : 'Download Template'}
            </button>
          </div>

          {/* Step 2 — upload */}
          <div className="p-4 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-gray-800">2. Upload the filled file</p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {fileName ? `Selected: ${fileName}` : '.xlsx, .xlsm or .csv — the sheet named "Employees" is read.'}
                </p>
              </div>
              <label className={`${BTN_GHOST} shrink-0 cursor-pointer`}>
                <Upload size={13} /> Choose File
                <input
                  type="file"
                  accept=".xlsx,.xlsm,.xls,.csv"
                  className="hidden"
                  disabled={importing}
                  onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
                />
              </label>
            </div>

            {parseError && (
              <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200">
                <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" />
                <p className="text-[12px] text-red-700">{parseError}</p>
              </div>
            )}
          </div>

          {/* Step 3 — what was read */}
          {rows.length > 0 && !results && (
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-gray-900">3. Check the rows</h3>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                    <CheckCircle2 size={11} /> {valid.length} ready
                  </span>
                  {invalid.length > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-50 text-red-700 font-semibold">
                      <AlertCircle size={11} /> {invalid.length} with problems
                    </span>
                  )}
                </div>
              </div>

              <div className="overflow-auto max-h-[38vh]">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-[12px] font-semibold text-gray-500 whitespace-nowrap">Row</th>
                      {previewCols.map((c) => (
                        <th key={c.key} className="px-4 py-3 text-left text-[12px] font-semibold text-gray-500 whitespace-nowrap">
                          {c.header}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-left text-[12px] font-semibold text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r._row}
                        className={`border-b border-gray-100 last:border-0 ${r.errors.length ? 'bg-red-50/40' : 'hover:bg-gray-50'}`}>
                        <td className="px-4 py-3 text-gray-400 tabular-nums">{r._row}</td>
                        {previewCols.map((c) => (
                          <td key={c.key} className="px-4 py-3 text-gray-700 whitespace-nowrap">
                            {r.values[c.key] || <span className="text-gray-300">—</span>}
                          </td>
                        ))}
                        <td className="px-4 py-3">
                          {r.errors.length === 0 ? (
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
                              <CheckCircle2 size={12} /> Ready
                            </span>
                          ) : (
                            <ul className="space-y-0.5">
                              {r.errors.map((e, i) => (
                                <li key={i} className="text-[11px] text-red-700 flex items-start gap-1">
                                  <span className="text-red-400">•</span>{e}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {invalid.length > 0 && (
                <div className="px-5 py-3 border-t border-gray-200 bg-amber-50">
                  <p className="text-[11px] text-amber-800">
                    Rows with problems are skipped. Fix them in the file and upload it again —
                    importing twice is safe, because a Staff ID that already exists is rejected.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3.5 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900">Import finished</h3>
              </div>
              <div className="px-5 py-4 space-y-3">
                <div className="flex items-center gap-2 text-[13px]">
                  <CheckCircle2 size={15} className="text-emerald-600" />
                  <span className="font-semibold text-gray-800">{results.ok.length} employee{results.ok.length !== 1 ? 's' : ''} added</span>
                  <span className="text-gray-500">— waiting in the Approvals tab.</span>
                </div>

                {results.failed.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-[13px] mb-1.5">
                      <AlertCircle size={15} className="text-red-600" />
                      <span className="font-semibold text-gray-800">{results.failed.length} could not be added</span>
                    </div>
                    <ul className="space-y-1">
                      {results.failed.map((f) => (
                        <li key={f._row} className="text-[11px] text-red-700">
                          Row {f._row} ({f.values.serviceNo || 'no Staff ID'}): {f.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
          <button onClick={refreshLookups} disabled={loadingLookups || importing} className={BTN_GHOST}>
            <RefreshCw size={13} className={loadingLookups ? 'animate-spin' : ''} /> Reload lists
          </button>

          <div className="flex items-center gap-2">
            {importing && (
              <span className="text-[11px] text-gray-500 tabular-nums mr-1">
                Importing {progress.done} of {progress.total}…
              </span>
            )}
            <button onClick={onClose} disabled={importing} className={BTN_GHOST}>
              {results ? 'Close' : 'Cancel'}
            </button>
            {!results && (
              <button onClick={runImport} disabled={importing || valid.length === 0} className={BTN_PRIMARY}>
                {importing
                  ? <><Loader2 size={13} className="animate-spin" /> Importing…</>
                  : <>Import {valid.length > 0 ? valid.length : ''} Employee{valid.length !== 1 ? 's' : ''} <ChevronRight size={13} /></>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}